import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Media, Patient } from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  PrimaryKey,
  Timestamp,
  getError,
  toTimestamp,
  type DocumentReference,
  type WithRef,
  FirestoreMigrate,
  toMoment,
  resolveSequentially,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { CopyFiles, StorageFile, getBucketStoragePath } from '../../../storage';
import { TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PatientDestinationEntity } from './patients';
import {
  OasisPatientLetter,
  PatientLetterSourceEntity,
} from '../../source/entities/patient-letters';
import { compact } from 'lodash';
import { getBucketFileCreatedAt } from '../../../destination/entities/patient-files';

export const PATIENT_DOCUMENTS_RESOURCE_TYPE = 'patientDocuments';
export const PATIENT_LETTERS_RESOURCE_TYPE = 'patientLetters';

export const PATIENT_DOCUMENTS_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_DOCUMENTS_RESOURCE_TYPE,
    label: 'Patient Documents',
    description: ``,
  },
});

export interface IPatientFileDestinationRecord {
  fileRefs: DocumentReference<IMedia>[];
}

export interface IPatientDocumentJobData {
  sourcePatient: IGetRecordResponse<OasisPatient>;
}

export interface IPatientFileMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  documents: IPatientFileData[];
  letters: IGetRecordResponse<OasisPatientLetter>[];
}

export interface IPatientFileData {
  name: string;
  filePath: string;
  createdAt: Timestamp;
}

export class PatientDocumentDestinationEntity extends BaseDestinationEntity<
  IPatientFileDestinationRecord,
  IPatientDocumentJobData,
  IPatientFileMigrationData
> {
  destinationEntity = PATIENT_DOCUMENTS_DESTINATION_ENTITY;
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientLetters: new PatientLetterSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override filters = [
    new PatientIdFilter<IPatientDocumentJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  sourceCountDataAccessor(
    data: IPatientDocumentJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: IPatientDocumentJobData): string {
    return data.sourcePatient.record.uid;
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientFileMigrationData
  ): IPatientFileMigrationData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    _migrationData: IPatientFileMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientDocumentJobData[]> {
    return this.buildSourceRecordQuery$(
      migration,
      this.sourceEntities.patients,
      runOptions
    ).pipe(
      map((sourcePatients) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          destinationEntity,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientDocumentJobData
  ): Promise<
    | IPatientFileMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this.buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient`
        );
      }

      const documents = await this.getPatientFiles(
        migration,
        sourcePatientId.toString()
      );

      const letters = await this.sourceEntities.patientLetters.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      if (!documents.length && !letters.length) {
        return this.buildSkippedResponse(data);
      }

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        documents,
        letters,
      };
    } catch (error) {
      return this.buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  async getPatientFiles(
    migration: WithRef<IPracticeMigration>,
    patientId: string
  ): Promise<IPatientFileData[]> {
    const splitId = patientId.split('');
    while (splitId.length < 6) {
      splitId.unshift('0');
    }
    const patientFolderPath = splitId.join('/');

    const projectId = migration.configuration.projectId;
    const bucketName = getBucketStoragePath(migration.source);
    const bucketPath = `files/${patientFolderPath}`;

    const storage = new CopyFiles(bucketName, bucketPath, '', '', projectId)
      .storage;

    const [files] = await storage.bucket(bucketName).getFiles({
      prefix: bucketPath,
    });

    return files.map((file) => ({
      name: file.name.split('/').pop() ?? file.name,
      filePath: file.name,
      createdAt: getBucketFileCreatedAt(file) ?? toTimestamp(),
    }));
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    migrationData: IPatientFileMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const documentRefs = await this.addDocumentsToPatient(
        migration,
        translationMapHandler,
        migrationData
      );

      const letterRefs = await this.addLettersToPatient(
        migration,
        translationMapHandler,
        migrationData
      );
      return this._buildSuccessResponse(jobData.sourcePatient, [
        ...documentRefs,
        ...letterRefs,
      ]);
    } catch (error) {
      return this.buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  async addDocumentsToPatient(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    migrationData: IPatientFileMigrationData
  ): Promise<DocumentReference<IMedia>[]> {
    const copyStorage = this.getCopyStorage(
      getBucketStoragePath(migration.source),
      migrationData.patientRef,
      migration.configuration.projectId,
      migration.configuration.destinationBucket
    );

    return resolveSequentially(migrationData.documents, async (document) => {
      const filePath = await copyStorage.copy(document.filePath);
      if (!filePath) {
        throw new Error(`Failed to copy file: ${document.filePath}`);
      }

      const fileDestinationRef = await translationMap.getDestination(
        document.filePath,
        PATIENT_DOCUMENTS_RESOURCE_TYPE
      );

      const mediaRef = await FirestoreMigrate.upsertDoc(
        Patient.mediaCol({ ref: migrationData.patientRef }),
        {
          ...Media.init({
            name: document.name,
            path: filePath,
          }),
          createdAt: document.createdAt,
          deleted: false,
        },
        fileDestinationRef?.id
      );

      if (!fileDestinationRef) {
        await translationMap.upsert({
          sourceIdentifier: document.filePath,
          destinationIdentifier: mediaRef,
          resourceType: PATIENT_DOCUMENTS_RESOURCE_TYPE,
        });
      }

      return mediaRef;
    });
  }

  async addLettersToPatient(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    migrationData: IPatientFileMigrationData
  ): Promise<DocumentReference<IMedia>[]> {
    const copyStorage = this.getCopyStorage(
      getBucketStoragePath(migration.source),
      migrationData.patientRef,
      migration.configuration.projectId,
      migration.configuration.destinationBucket
    );

    return compact(
      await resolveSequentially(migrationData.letters, async (letter) => {
        const filePath = await copyStorage.copy(
          `files/${letter.data.fileName}`
        );
        if (!filePath) {
          // eslint-disable-next-line no-console
          console.warn(`Failed to copy file: ${letter.data.fileName}`);
          return;
        }

        const fileDestinationRef = await translationMap.getDestination(
          letter.data.id.toString(),
          PATIENT_LETTERS_RESOURCE_TYPE
        );

        const mediaRef = await FirestoreMigrate.upsertDoc(
          Patient.mediaCol({ ref: migrationData.patientRef }),
          {
            ...Media.init({
              name: letter.data.fileName,
              path: filePath,
            }),
            createdAt: toTimestamp(toMoment(letter.data.createdAt)),
            deleted: false,
          },
          fileDestinationRef?.id
        );

        if (!fileDestinationRef) {
          await translationMap.upsert({
            sourceIdentifier: letter.data.id.toString(),
            destinationIdentifier: mediaRef,
            resourceType: PATIENT_LETTERS_RESOURCE_TYPE,
          });
        }

        return mediaRef;
      })
    );
  }

  getCopyStorage(
    sourceBucket: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): StorageFile {
    return new StorageFile(
      sourceBucket,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );
  }

  buildErrorResponse(
    sourcePatient: IGetRecordResponse<OasisPatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  buildSkippedResponse(
    data: IPatientDocumentJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<OasisPatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientFileDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
